import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Badge,
  Alert,
  Input,
  InputGroup,
  InputGroupText,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from 'reactstrap';
import classnames from 'classnames';
import DataTable from '../../components/DataTable';
import Loading from '../../components/Loading';
import { inventarioService } from '../../services/inventarioService';
import { sucursalesService } from '../../services/sucursalesService';
import { productosService } from '../../services/productosService';
import { empresasService } from '../../services/empresasService';
import { useAuth } from '../../contexts/AuthContext';
import MovimientoForm from './MovimientoForm';
import ConteoFisicoForm from './ConteoFisicoForm';
import SnowFallComponent from 'components/SnowFallComponent';

function Inventario() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('1');
  const [empresa, setEmpresa] = useState(null);
  const [sucursales, setSucursales] = useState([]);
  const [sucursalSeleccionada, setSucursalSeleccionada] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingSucursales, setLoadingSucursales] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estado para Inventario
  const [inventario, setInventario] = useState([]);
  const [soloStockBajo, setSoloStockBajo] = useState(false);
  const [buscarInventario, setBuscarInventario] = useState('');
  const [showMovimientoForm, setShowMovimientoForm] = useState(false);
  const [showConteoForm, setShowConteoForm] = useState(false);
  const [movimientoFormKey, setMovimientoFormKey] = useState(0); // Key para forzar remontaje del formulario
  const [conteoFormKey, setConteoFormKey] = useState(0); // Key para forzar remontaje del formulario

  // Estado para Movimientos
  const [movimientos, setMovimientos] = useState([]);
  const [busquedaMovimientos, setBusquedaMovimientos] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

  // Estado para Conteos Físicos
  const [conteosFisicos, setConteosFisicos] = useState([]);
  const [busquedaConteos, setBusquedaConteos] = useState('');

  useEffect(() => {
    loadEmpresaYSucursales();
  }, []);

  useEffect(() => {
    if (!loadingSucursales && sucursalSeleccionada) {
      if (activeTab === '1') {
        loadInventario();
      } else if (activeTab === '2') {
        loadMovimientos();
      } else if (activeTab === '3') {
        loadConteosFisicos();
      }
    }
  }, [sucursalSeleccionada, soloStockBajo, buscarInventario, activeTab, loadingSucursales]);

  useEffect(() => {
    if (activeTab === '2' && sucursalSeleccionada) {
      loadMovimientos();
    } else if (activeTab === '3' && sucursalSeleccionada) {
      loadConteosFisicos();
    }
  }, [busquedaMovimientos, busquedaConteos, filtroFechaDesde, filtroFechaHasta, activeTab, sucursalSeleccionada]);

  const loadEmpresaYSucursales = async () => {
    try {
      setLoadingSucursales(true);
      const empresasResponse = await empresasService.getAll();
      if (empresasResponse.success && empresasResponse.data) {
        const empresasData = Array.isArray(empresasResponse.data) ? empresasResponse.data : [];
        const empresaActiva = empresasData.find(emp => emp.Activo !== false && emp.activo !== false) || empresasData[0];
        if (empresaActiva) {
          setEmpresa(empresaActiva);
          const sucursalesResponse = await sucursalesService.getAll(empresaActiva.EmpresaID || empresaActiva.empresaID);
          if (sucursalesResponse.success && sucursalesResponse.data) {
            const sucursalesData = Array.isArray(sucursalesResponse.data) ? sucursalesResponse.data : [];
            const normalized = sucursalesData.map(suc => ({
              SucursalID: suc.SucursalID || suc.sucursalID,
              Nombre: suc.Nombre || suc.nombre || '',
            }));
            setSucursales(normalized);
            if (normalized.length > 0) {
              setSucursalSeleccionada(normalized[0].SucursalID.toString());
            }
          }
        }
      }
    } catch (err) {
      setError('Error al cargar datos de la empresa');
    } finally {
      setLoadingSucursales(false);
    }
  };

  const loadInventario = async () => {
    try {
      setLoading(true);
      setError('');
      const sucursalId = parseInt(sucursalSeleccionada);
      if (!sucursalId) return;

      const response = await inventarioService.getBySucursal(sucursalId, soloStockBajo);
      if (response.success && response.data) {
        const inventarioData = Array.isArray(response.data) ? response.data : [];
        let normalized = inventarioData.map(inv => {
          const stockActual = inv.StockActual !== undefined ? inv.StockActual : (inv.stockActual !== undefined ? inv.stockActual : 0);
          const stockMinimo = inv.StockMinimo !== undefined ? inv.StockMinimo : (inv.stockMinimo !== undefined ? inv.stockMinimo : 0);
          return {
            InventarioID: inv.InventarioID || inv.inventarioID,
            ProductoID: inv.ProductoID || inv.productoID,
            ProductoNombre: inv.ProductoNombre || inv.productoNombre || '',
            ProductoSKU: inv.ProductoSKU || inv.productoSKU || '',
            SucursalID: inv.SucursalID || inv.sucursalID,
            SucursalNombre: inv.SucursalNombre || inv.sucursalNombre || '',
            StockActual: parseFloat(stockActual) || 0,
            StockMinimo: parseFloat(stockMinimo) || 0,
            StockBajo: inv.StockBajo !== undefined ? inv.StockBajo : (inv.stockBajo !== undefined ? inv.stockBajo : false),
            FechaUltimoMovimiento: inv.FechaUltimoMovimiento || inv.fechaUltimoMovimiento,
          };
        });

        // Filtrar por búsqueda si hay texto
        if (buscarInventario.trim()) {
          const searchTerm = buscarInventario.toLowerCase().trim();
          normalized = normalized.filter(inv => {
            const nombre = (inv.ProductoNombre || '').toLowerCase();
            const sku = (inv.ProductoSKU || '').toLowerCase();
            return nombre.includes(searchTerm) || sku.includes(searchTerm);
          });
        }

        setInventario(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar inventario');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };


  const loadMovimientos = async () => {
    try {
      setLoading(true);
      setError('');
      const sucursalId = parseInt(sucursalSeleccionada);
      if (!sucursalId) return;

      const response = await inventarioService.getMovimientos(
        sucursalId,
        null,
        filtroFechaDesde || null,
        filtroFechaHasta || null,
        null
      );

      if (response.success && response.data) {
        const movimientosData = Array.isArray(response.data) ? response.data : [];
        let normalized = movimientosData.map(mov => {
          const cantidad = mov.Cantidad !== undefined ? mov.Cantidad : (mov.cantidad !== undefined ? mov.cantidad : 0);
          return {
            MovimientoID: mov.MovimientoID || mov.movimientoID,
            TipoMovimientoID: mov.TipoMovimientoID || mov.tipoMovimientoID,
            TipoMovimientoNombre: mov.TipoMovimientoNombre || mov.tipoMovimientoNombre || '',
            EsEntrada: mov.EsEntrada !== undefined ? mov.EsEntrada : (mov.esEntrada !== undefined ? mov.esEntrada : false),
            ProductoID: mov.ProductoID || mov.productoID,
            ProductoNombre: mov.ProductoNombre || mov.productoNombre || '',
            SucursalOrigenID: mov.SucursalOrigenID || mov.sucursalOrigenID,
            SucursalOrigenNombre: mov.SucursalOrigenNombre || mov.sucursalOrigenNombre || '',
            SucursalDestinoID: mov.SucursalDestinoID || mov.sucursalDestinoID,
            SucursalDestinoNombre: mov.SucursalDestinoNombre || mov.sucursalDestinoNombre,
            Cantidad: parseFloat(cantidad) || 0,
            UsuarioID: mov.UsuarioID || mov.usuarioID,
            UsuarioNombre: mov.UsuarioNombre || mov.usuarioNombre || '',
            Observaciones: mov.Observaciones || mov.observaciones,
            FechaMovimiento: mov.FechaMovimiento || mov.fechaMovimiento,
          };
        });

        if (busquedaMovimientos.trim()) {
          const searchTerm = busquedaMovimientos.toLowerCase().trim();
          normalized = normalized.filter(mov => {
            const nombre = (mov.ProductoNombre || '').toLowerCase();
            return nombre.includes(searchTerm);
          });
        }

        setMovimientos(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar movimientos');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const loadConteosFisicos = async () => {
    try {
      setLoading(true);
      setError('');
      const sucursalId = parseInt(sucursalSeleccionada);
      if (!sucursalId) return;

      const response = await inventarioService.getConteosFisicos(
        sucursalId,
        null, // No filtrar por producto en la vista general
        filtroFechaDesde || null,
        filtroFechaHasta || null
      );

      if (response.success && response.data) {
        const conteosData = Array.isArray(response.data) ? response.data : [];

        let normalized = conteosData.map(conteo => ({
          ConteoID: conteo.ConteoID || conteo.conteoID,
          SucursalID: conteo.SucursalID || conteo.sucursalID,
          SucursalNombre: conteo.SucursalNombre || conteo.sucursalNombre || '',
          ProductoID: conteo.ProductoID || conteo.productoID,
          ProductoNombre: conteo.ProductoNombre || conteo.productoNombre || '',
          ProductoSKU: conteo.ProductoSKU || conteo.productoSKU || '',
          StockTeorico: conteo.StockTeorico !== undefined ? conteo.StockTeorico : (conteo.stockTeorico !== undefined ? conteo.stockTeorico : 0),
          StockFisico: conteo.StockFisico !== undefined ? conteo.StockFisico : (conteo.stockFisico !== undefined ? conteo.stockFisico : 0),
          Diferencia: conteo.Diferencia !== undefined ? conteo.Diferencia : (conteo.diferencia !== undefined ? conteo.diferencia : 0),
          FechaConteo: conteo.FechaConteo || conteo.fechaConteo,
          UsuarioID: conteo.UsuarioID || conteo.usuarioID,
          UsuarioNombre: conteo.UsuarioNombre || conteo.usuarioNombre || '',
          Observaciones: conteo.Observaciones || conteo.observaciones
        }));
        if (busquedaConteos.trim()) {
          const searchTerm = busquedaConteos.toLowerCase().trim();
          normalized = normalized.filter(conteo => {
            const nombre = (conteo.ProductoNombre || '').toLowerCase();
            const sku = (conteo.ProductoSKU || '').toLowerCase();
            return nombre.includes(searchTerm) || sku.includes(searchTerm);
          });
        }

        setConteosFisicos(normalized);
      } else {
        setError(response.Message || response.message || 'Error al cargar conteos físicos');
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  const handleFormSuccess = () => {
    setShowMovimientoForm(false);
    setShowConteoForm(false);
    setSuccess('Operación realizada exitosamente');
    setTimeout(() => setSuccess(''), 3000);
    if (activeTab === '1') {
      loadInventario();
    } else if (activeTab === '2') {
      loadMovimientos();
    } else if (activeTab === '3') {
      loadConteosFisicos();
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const inventarioColumns = [
    {
      key: 'productoNombre',
      label: 'Producto',
      accessor: 'ProductoNombre',
    },
    {
      key: 'sku',
      label: 'SKU',
      accessor: 'ProductoSKU',
    },
    {
      key: 'stockActual',
      label: 'Stock Actual',
      accessor: 'StockActual',
      render: (value) => value !== undefined && value !== null ? parseFloat(value).toFixed(2) : '0.00',
    },
    {
      key: 'stockMinimo',
      label: 'Stock Mínimo',
      accessor: 'StockMinimo',
      render: (value) => value !== undefined && value !== null ? parseFloat(value).toFixed(2) : '0.00',
    },
    {
      key: 'stockBajo',
      label: 'Estado',
      accessor: 'StockBajo',
      render: (value) => (
        <Badge color={value ? 'danger' : 'success'}>
          {value ? 'Stock Bajo' : 'Normal'}
        </Badge>
      ),
    },
    {
      key: 'fechaUltimoMovimiento',
      label: 'Último Movimiento',
      accessor: 'FechaUltimoMovimiento',
      render: (value) => formatDate(value),
    },
  ];

  const movimientosColumns = [
    {
      key: 'fechaMovimiento',
      label: 'Fecha',
      accessor: 'FechaMovimiento',
      render: (value) => formatDate(value),
    },
    {
      key: 'tipoMovimiento',
      label: 'Tipo',
      accessor: 'TipoMovimientoNombre',
    },
    {
      key: 'producto',
      label: 'Producto',
      accessor: 'ProductoNombre',
    },
    {
      key: 'cantidad',
      label: 'Cantidad',
      accessor: 'Cantidad',
      render: (value) => {
        const numValue = value !== undefined && value !== null ? parseFloat(value) : 0;
        return (
          <Badge color={numValue > 0 ? 'success' : 'danger'}>
            {numValue > 0 ? '+' : ''}{numValue.toFixed(2)}
          </Badge>
        );
      },
    },
    {
      key: 'sucursalOrigen',
      label: 'Sucursal Origen',
      accessor: 'SucursalOrigenNombre',
    },
    {
      key: 'sucursalDestino',
      label: 'Sucursal Destino',
      accessor: 'SucursalDestinoNombre',
      render: (value) => value || '-',
    },
    {
      key: 'usuario',
      label: 'Usuario',
      accessor: 'UsuarioNombre',
    },
    {
      key: 'observaciones',
      label: 'Observaciones',
      accessor: 'Observaciones',
      render: (value) => value || '-',
    },
  ];

  const conteosColumns = [
    {
      key: 'fechaConteo',
      label: 'Fecha',
      accessor: 'FechaConteo',
      render: (value) => formatDate(value),
    },
    {
      key: 'producto',
      label: 'Producto',
      accessor: 'ProductoNombre',
    },
    {
      key: 'sku',
      label: 'SKU',
      accessor: 'ProductoSKU',
    },
    {
      key: 'teorico',
      label: 'Teórico',
      accessor: 'StockTeorico',
      render: (value) => value !== undefined && value !== null ? parseFloat(value).toFixed(2) : '0.00',
    },
    {
      key: 'fisico',
      label: 'Físico',
      accessor: 'StockFisico',
      render: (value) => value !== undefined && value !== null ? parseFloat(value).toFixed(2) : '0.00',
    },
    {
      key: 'diferencia',
      label: 'Diferencia',
      accessor: 'Diferencia',
      render: (value) => {
        const numValue = value !== undefined && value !== null ? parseFloat(value) : 0;
        return (
          <Badge color={numValue === 0 ? 'success' : (numValue > 0 ? 'info' : 'danger')}>
            {numValue > 0 ? '+' : ''}{numValue.toFixed(2)}
          </Badge>
        );
      },
    },
    {
      key: 'usuario',
      label: 'Usuario',
      accessor: 'UsuarioNombre',
    },
    {
      key: 'observaciones',
      label: 'Observaciones',
      accessor: 'Observaciones',
    },
  ];

  return (
    <div className="content">
      <SnowFallComponent />
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <Row>
                <Col>
                  <CardTitle tag="h4">Inventario</CardTitle>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md="4">
                  <select
                    className="form-control"
                    value={sucursalSeleccionada}
                    onChange={(e) => setSucursalSeleccionada(e.target.value)}
                    disabled={loadingSucursales}
                  >
                    <option value="">Seleccione una sucursal</option>
                    {sucursales.map((suc) => (
                      <option key={suc.SucursalID} value={suc.SucursalID}>
                        {suc.Nombre}
                      </option>
                    ))}
                  </select>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {success && (
                <Alert color="success" className="mb-3">
                  {success}
                </Alert>
              )}

              <Nav tabs>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '1' })}
                    onClick={() => setActiveTab('1')}
                    style={{ cursor: 'pointer' }}
                  >
                    Inventario
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '2' })}
                    onClick={() => setActiveTab('2')}
                    style={{ cursor: 'pointer' }}
                  >
                    Movimientos
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '3' })}
                    onClick={() => setActiveTab('3')}
                    style={{ cursor: 'pointer' }}
                  >
                    Conteos Físicos
                  </NavLink>
                </NavItem>
              </Nav>

              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <Row>
                    <Col md="12">
                      <div className="mt-3">
                        <Row className="mb-3">
                          <Col md="6">
                            <div className="form-check">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                checked={soloStockBajo}
                                onChange={(e) => setSoloStockBajo(e.target.checked)}
                                id="soloStockBajo"
                              />
                              <label className="form-check-label" htmlFor="soloStockBajo">
                                Solo productos con stock bajo
                              </label>
                            </div>
                          </Col>
                          <Col md="6" className="text-right">
                            <Button
                              color="primary"
                              onClick={() => {
                                setMovimientoFormKey(prev => prev + 1); // Incrementar key para forzar remontaje
                                setShowMovimientoForm(true);
                              }}
                              disabled={!sucursalSeleccionada}
                            >
                              <i className="tim-icons icon-simple-add" /> Nuevo Movimiento
                            </Button>
                            <Button
                              color="info"
                              className="ml-2"
                              onClick={() => {
                                setConteoFormKey(prev => prev + 1); // Incrementar key para forzar remontaje
                                setShowConteoForm(true);
                              }}
                              disabled={!sucursalSeleccionada}
                            >
                              <i className="tim-icons icon-check-2" /> Conteo Físico
                            </Button>
                          </Col>
                        </Row>
                        <Row>
                          <Col md="6">
                            <InputGroup>
                              <InputGroupText>
                                <i className="tim-icons icon-zoom-split" />
                              </InputGroupText>
                              <Input
                                type="text"
                                placeholder="Buscar por nombre o SKU..."
                                value={buscarInventario}
                                onChange={(e) => setBuscarInventario(e.target.value)}
                              />
                            </InputGroup>
                          </Col>
                        </Row>
                        {loading ? (
                          <Loading />
                        ) : (
                          <DataTable columns={inventarioColumns} data={inventario} />
                        )}
                      </div>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="2">
                  <Row>
                    <Col md="12">
                      <div className="mt-3">
                        <Row className="mb-3">
                          <Col md="3">
                            <InputGroup>
                              <InputGroupText>
                                <i className="tim-icons icon-zoom-split" />
                              </InputGroupText>
                              <Input
                                type="text"
                                placeholder="Buscar producto..."
                                value={busquedaMovimientos}
                                onChange={(e) => setBusquedaMovimientos(e.target.value)}
                              />
                            </InputGroup>
                          </Col>
                          <Col md="3">
                            <Input
                              type="date"
                              placeholder="Fecha desde"
                              value={filtroFechaDesde}
                              onChange={(e) => setFiltroFechaDesde(e.target.value)}
                            />
                          </Col>
                          <Col md="3">
                            <Input
                              type="date"
                              placeholder="Fecha hasta"
                              value={filtroFechaHasta}
                              onChange={(e) => setFiltroFechaHasta(e.target.value)}
                            />
                          </Col>
                          <Col md="3">
                            <Button
                              color="primary"
                              onClick={loadMovimientos}
                              disabled={!sucursalSeleccionada}
                            >
                              <i className="tim-icons icon-zoom-split" /> Buscar
                            </Button>
                          </Col>
                        </Row>
                        {loading ? (
                          <Loading />
                        ) : (
                          <DataTable columns={movimientosColumns} data={movimientos} />
                        )}
                      </div>
                    </Col>
                  </Row>
                </TabPane>

                <TabPane tabId="3">
                  <Row>
                    <Col md="12">
                      <div className="mt-3">
                        <Row className="mb-3">
                          <Col md="3">
                            <InputGroup>
                              <InputGroupText>
                                <i className="tim-icons icon-zoom-split" />
                              </InputGroupText>
                              <Input
                                type="text"
                                placeholder="Buscar producto..."
                                value={busquedaConteos}
                                onChange={(e) => setBusquedaConteos(e.target.value)}
                              />
                            </InputGroup>
                          </Col>
                          <Col md="3">
                            <Input
                              type="date"
                              placeholder="Fecha desde"
                              value={filtroFechaDesde}
                              onChange={(e) => setFiltroFechaDesde(e.target.value)}
                            />
                          </Col>
                          <Col md="3">
                            <Input
                              type="date"
                              placeholder="Fecha hasta"
                              value={filtroFechaHasta}
                              onChange={(e) => setFiltroFechaHasta(e.target.value)}
                            />
                          </Col>
                          <Col md="3" className="text-right">
                            <Button
                              color="primary"
                              onClick={loadConteosFisicos}
                              disabled={!sucursalSeleccionada}
                            >
                              <i className="tim-icons icon-zoom-split" /> Buscar
                            </Button>
                          </Col>
                        </Row>
                        {loading ? (
                          <Loading />
                        ) : (
                          <DataTable columns={conteosColumns} data={conteosFisicos} />
                        )}
                      </div>
                    </Col>
                  </Row>
                </TabPane>
              </TabContent>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {showMovimientoForm && (
        <MovimientoForm
          key={movimientoFormKey} // Key para forzar remontaje cuando cambia
          sucursalId={parseInt(sucursalSeleccionada)}
          usuarioId={user?.UsuarioID || user?.usuarioID}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowMovimientoForm(false);
            setMovimientoFormKey(prev => prev + 1); // Incrementar key para resetear el formulario la próxima vez
          }}
        />
      )}

      {showConteoForm && (
        <ConteoFisicoForm
          key={conteoFormKey} // Key para forzar remontaje cuando cambia
          sucursalId={parseInt(sucursalSeleccionada)}
          usuarioId={user?.UsuarioID || user?.usuarioID}
          onSuccess={handleFormSuccess}
          onCancel={() => {
            setShowConteoForm(false);
            setConteoFormKey(prev => prev + 1); // Incrementar key para resetear el formulario la próxima vez
          }}
        />
      )}
    </div>
  );
}

export default Inventario;

